import { createSyncEngine, syncLockName, type SyncEngine } from "@njbsoft/field-sync";
import { resolveLockManager } from "./locks";
import {
  claimFieldworkStorageForTeardown,
  getFieldworkStorage,
  resetFieldworkStorage,
} from "./storage";
import { createCompanyFieldTransport } from "./transport";

// Module-level accessors resolved by the FieldworkStorageProvider (and the
// logout teardown) ONLY — no component resolves an engine or derives a profile
// id for one itself.

export { getFieldworkProposalCure, getFieldworkStorage, hasOpenFieldworkStorage } from "./storage";

interface EngineEntry {
  profileId: string;
  engine: Promise<SyncEngine>;
}

let current: EngineEntry | null = null;

function buildEngine(profileId: string): Promise<SyncEngine> {
  return getFieldworkStorage(profileId).then((storage) =>
    createSyncEngine({
      storage,
      transport: createCompanyFieldTransport(),
      locks: resolveLockManager(),
      profileId,
      // The tester persona is org-less by construction: the portal binds the
      // field arm only, so the engine's org-mode phases are unreachable here
      // rather than absent.
      resolveScope: () => ({ mode: "field" }),
    }),
  );
}

/**
 * One engine per profile, memoized; a different profile id drops the prior
 * entry (and the storage module closes the prior database on the same switch).
 * A rejected build evicts itself so the next call retries instead of replaying
 * the cached rejection.
 */
export function getFieldworkEngine(profileId: string): Promise<SyncEngine> {
  if (current !== null && current.profileId !== profileId) current = null;
  if (current === null) {
    const entry: EngineEntry = { profileId, engine: buildEngine(profileId) };
    current = entry;
    void entry.engine.catch(() => {
      if (current === entry) current = null;
    });
  }
  return current.engine;
}

/** Drops the memoized engine and storage handles (logout / profile switch). */
export function resetFieldworkEngine(): void {
  current = null;
  resetFieldworkStorage();
}

let teardownInFlight: { profileId: string; done: Promise<void> } | null = null;

/**
 * Teardown for an explicit sign-out or a server-rejected refresh. The outbox,
 * drafts and evidence SURVIVE while unsynced — that is the regulatory promise,
 * and the queue is drainable only by its owning profile. `hasUnsyncedWork` is
 * the drained-ness verdict, covering every unsynced family — the four outbox
 * queues, drafts and staged draft attachments — never `pendingCount`, the
 * sync-bar counter that reads only two of them. A fully drained profile has nothing left to promise, so
 * its database goes entirely; any unsynced work instead drops the bundle
 * snapshot MINUS the rows that work still points at, because the sealed outbox
 * document renders from the bundle's version definition and a full drop would
 * strand queued capture unrenderable.
 *
 * Both sign-out arms funnel through here via `clearCompanySession`: the Sign
 * Out buttons call it directly, and a server-rejected refresh reaches it
 * through `RefreshManager.onRefreshFailed` and every client's `onUnauthorized`
 * — there is no separate rejected-refresh teardown path to wire.
 *
 * The persisted database outlives any one tab's handle, so the teardown opens
 * one on demand when this JS context holds none — a sign-out from a session
 * that never mounted the tester subtree still drops the bundle snapshot. The
 * storage mutation runs under the profile's sync lock (the same mechanism the
 * engine drains under), so it never interleaves with an in-flight run here or
 * in a sibling tab; concurrent sign-out calls coalesce on the in-flight
 * teardown instead of double-purging.
 */
export function tearDownFieldworkForLogout(profileId: string): Promise<void> {
  if (teardownInFlight !== null && teardownInFlight.profileId === profileId) {
    return teardownInFlight.done;
  }
  const done = runTeardown(profileId).finally(() => {
    teardownInFlight = null;
  });
  teardownInFlight = { profileId, done };
  return done;
}

async function runTeardown(profileId: string): Promise<void> {
  const handle = claimFieldworkStorageForTeardown(profileId);
  await resolveLockManager().withExclusiveLock(syncLockName(profileId), async () => {
    const { adapter, db } = await handle;
    if (await adapter.hasUnsyncedWork()) {
      await adapter.dropBundleSnapshotPreservingUnsyncedResidue();
    } else {
      await adapter.purgeProfileDatabase();
    }
    db.close();
  });
  resetFieldworkEngine();
}
